import React, {useEffect, useState} from "react";
import {useRouteNavigator} from "@vkontakte/vk-mini-apps-router";
import {Icon28TargetOutline} from "@vkontakte/icons";
import {fetchActiveMissions} from "../../services/missions";

const READY_BADGE_CLASSES = "arcade-badge absolute top-2 right-2 min-w-[24px]";

// normalizeMission приводит данные миссии к безопасным значениям для главного экрана.
function normalizeMission(mission) {
    return {
        ...mission,
        title: mission.title ?? "",
        progress: Math.max(mission.progress ?? 0, 0),
        targetValue: Math.max(mission.targetValue ?? 0, 0),
        status: mission.status ?? "active"
    };
}

// getMissionRatio считает прогресс миссии, чтобы выбрать самую близкую цель игрока.
function getMissionRatio(mission) {
    if (mission.targetValue <= 0) {
        return 0;
    }

    return Math.min(1, mission.progress / mission.targetValue);
}

// chooseNextMission выбирает готовую награду или ближайшую незавершенную миссию.
function chooseNextMission(missions) {
    const visibleMissions = missions
        .map(normalizeMission)
        .filter(mission => mission.status !== "claimed" && mission.status !== "expired");

    const completedMission = visibleMissions.find(mission => mission.status === "completed");
    if (completedMission) {
        return completedMission;
    }

    return visibleMissions
        .filter(mission => mission.targetValue > 0)
        .sort((left, right) => getMissionRatio(right) - getMissionRatio(left))[0] ?? null;
}

// Баннер с краткой информацией, чтобы перейти на отдельную панель миссий.
export default function MissionsBanner() {
    const routeNavigator = useRouteNavigator();
    const handleClick = () => routeNavigator.push('/missions');
    // Храним количество миссий с готовой наградой, чтобы отобразить бейдж.
    const [claimableCount, setClaimableCount] = useState(0);
    const [nextMission, setNextMission] = useState(null);

    useEffect(() => {
        // Загружаем статус миссий для отображения количества наград на главной.
        fetchActiveMissions()
            .then(resp => {
                const payload = resp.data?.response || {daily: [], weekly: []};
                const allMissions = [...(payload.daily || []), ...(payload.weekly || [])];
                const readyCount = allMissions.filter(mission => mission.status === "completed").length;
                setClaimableCount(readyCount);
                setNextMission(chooseNextMission(allMissions));
            })
            .catch(err => {
                console.error("missions banner load error", err);
                setClaimableCount(0);
                setNextMission(null);
            });
    }, []);

    const missionProgress = nextMission
        ? Math.min(100, Math.round(getMissionRatio(nextMission) * 100))
        : 0;
    // Выполненная миссия переключает баннер из режима прогресса в режим получения награды.
    const hasReadyReward = nextMission?.status === "completed";

    return (
        <div className="w-full cursor-pointer" onClick={handleClick}>
            <div className="arcade-panel p-4 space-y-4 relative">
                <div className="flex items-center justify-between gap-3">
                    <div className="arcade-icon-frame w-12 h-12 flex-shrink-0 flex items-center justify-center">
                        <Icon28TargetOutline/>
                    </div>
                    <div className={"text-gray-800 flex-1"}>
                        <p className="arcade-title text-base font-black">{hasReadyReward ? "Награда готова" : "Ближайшая цель"}</p>
                        <p className="text-sm">{nextMission?.title || "Следите за прогрессом по ежедневным и недельным целям"}</p>
                        {hasReadyReward ? (
                            <p className="mt-2 text-sm font-black text-ui-action">Забрать награду →</p>
                        ) : nextMission && (
                            <div className="mt-2">
                                <div className="flex justify-between text-[11px] text-gray-700">
                                    <span>{nextMission.progress}/{nextMission.targetValue}</span>
                                    <span>{missionProgress}%</span>
                                </div>
                                <div className="arcade-progress mt-1 h-2.5">
                                    <div className="arcade-progress__bar" style={{width: `${missionProgress}%`}}/>
                                </div>
                            </div>
                        )}
                    </div>
                    {claimableCount > 0 && (
                        <span className={READY_BADGE_CLASSES}>{claimableCount}</span>
                    )}
                </div>
            </div>
        </div>
    );
}
